import type {
  StudioThumbnailQueueItemOut,
  StudioThumbnailsOut,
  StudioThumbnailsRunOut,
} from '@/api/endpoints/scheduler'

/**
 * O aviso do "Aplicar capas pendentes", no card do canal (com o nome) e na
 * faixa (sem). Com `queued` (a fila de jobs, F4a), a drenagem espera a vez em
 * Jobs; sem ele (fila desligada, ou backend anterior), o texto de sempre.
 * `null` quando nada começou.
 */
export function coversRunMessage(
  data: Pick<StudioThumbnailsRunOut, 'started' | 'pending'> & Partial<Pick<StudioThumbnailsRunOut, 'queued' | 'job_id'>>,
  channelName?: string,
): string | null {
  if (!data.started) return null
  const covers = `${String(data.pending)} ${data.pending === 1 ? 'capa' : 'capas'}`
  if (data.queued === true) {
    return `${covers} na fila de jobs${channelName ? ` — ${channelName}` : ''}. Acompanhe em Jobs.`
  }
  if (channelName) return `Aplicando ${covers} — ${channelName}. Acompanhe em Jobs.`
  return `Drenagem iniciada com ${covers}. Acompanhe em Jobs.`
}

/** A manchete da faixa "Capas dos Shorts". */
export function statusLine(enabled: boolean, running: boolean, pending: number): string {
  if (!enabled) return 'A capa pelo Studio está desligada neste servidor'
  if (running) return 'Uma drenagem está rodando agora. Acompanhe em Jobs'
  if (pending === 0) return 'Nenhuma capa na fila'
  return `${String(pending)} ${pending === 1 ? 'capa' : 'capas'} na fila`
}

/**
 * O admin desligou a automação em Configurações → Prism Device: a faixa e os
 * selos de capa dos cards somem. O backend antigo (o Pages publica antes do
 * pull) não manda ``paused``, e aí vale ligada.
 */
export function isPaused(data: StudioThumbnailsOut | undefined): boolean {
  return data?.paused === true
}

/** O botão só habilita para o admin, com a capa ligada, fila não vazia e nada rodando. */
export function canRun(data: StudioThumbnailsOut): boolean {
  return data.enabled && !isPaused(data) && data.is_admin && !data.running && data.pending > 0
}

/**
 * O estado de um vídeo da fila. Falha que não é do vídeo (sessão, celular,
 * canal sem verificação) não gasta tentativa, então "falhou" com zero
 * tentativas é comum e diz outra coisa.
 */
export function attemptLabel(item: StudioThumbnailQueueItemOut, maxAttempts: number): string {
  if (item.status === 'pending') return 'Aguardando'
  if (item.attempts === 0) return 'Falhou · sem gastar tentativa'
  return `Falhou · ${String(item.attempts)} de ${String(maxAttempts)} tentativas`
}

/** Capas na fila por canal, para o selo de cada card. */
export function coversByChannel(queue: readonly StudioThumbnailQueueItemOut[]): Map<string, number> {
  const out = new Map<string, number>()
  for (const item of queue) out.set(item.channel_slug, (out.get(item.channel_slug) ?? 0) + 1)
  return out
}

export interface QueueSummary {
  failed: number
  /** O motivo mais comum entre as que falharam; vazio quando nenhuma falhou. */
  reason: string
}

/**
 * Quantas falharam e por quê, para a faixa recolhida. O erro da drenagem junta
 * as causas com "; ", e a última é a que diz o que fazer ("celular
 * indisponível: nenhum celular no servidor ADB").
 */
export function summarizeQueue(queue: readonly StudioThumbnailQueueItemOut[]): QueueSummary {
  const failed = queue.filter((item) => item.status === 'failed')
  const counts = new Map<string, number>()
  for (const item of failed) {
    const parts = item.error
      .split(';')
      .map((p) => p.trim())
      .filter(Boolean)
    const last = parts[parts.length - 1]
    if (last) counts.set(last, (counts.get(last) ?? 0) + 1)
  }
  let reason = ''
  let best = 0
  for (const [text, n] of counts) {
    if (n > best) {
      reason = text
      best = n
    }
  }
  return { failed: failed.length, reason }
}
